import { useEffect, useState, useCallback } from 'react';
import api from '../../api/index.js';
import s from './PriceSources.module.scss';
import Header from '../../components/Header/Header.jsx';
import Loader from '../../components/Loader/Loader.jsx';
import Popup from '../../components/Popup/Popup.jsx';
import Input from '../../components/Input/Input.jsx';
import delIcon from '../../assets/icons/delIcon.js';

const UseRrp = () => {
  const [brandsRrp, setBrandsRrp] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterInput, setFilterInput] = useState('');
  const [error, setError] = useState('');

  const [createShowed, setCreateShowed] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [manualName, setManualName] = useState('');
  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');
  const [selectedBrand, setSelectedBrand] = useState(null);

  const fetchRrp = useCallback(async () => {
    setLoading(true);
    setError('');
    const data = await api.priceSources.getUseRrp();
    setLoading(false);
    if (data.error) {
      setError(data.error);
      return;
    }
    setBrandsRrp(Array.isArray(data.brands) ? data.brands : []);
  }, []);

  useEffect(() => {
    fetchRrp();
  }, [fetchRrp]);

  const openCreate = async () => {
    setManualName('');
    setBrandSearch('');
    setSelectedBrand(null);
    setCreateError('');
    setCreateShowed(true);
    if (brands.length === 0) {
      setBrandsLoading(true);
      const data = await api.brands.getList();
      setBrandsLoading(false);
      if (data.brands) setBrands(data.brands);
    }
  };

  const handleSelectBrand = (brand) => {
    setSelectedBrand(brand);
    setBrandSearch(brand.brand_name);
    setManualName(brand.brand_name);
  };

  const handleCreate = async () => {
    const name = (manualName || selectedBrand?.brand_name || '').trim();
    if (!name) {
      setCreateError('Укажите или выберите бренд');
      return;
    }
    setCreateLoading(true);
    setCreateError('');
    const data = await api.priceSources.addUseRrpBrand(name);
    setCreateLoading(false);
    if (data.error) {
      setCreateError(data.error);
      return;
    }
    setBrandsRrp(Array.isArray(data.brands) ? data.brands : []);
    setCreateShowed(false);
  };

  const handleRemove = async (brand) => {
    const data = await api.priceSources.removeUseRrpBrand(brand);
    if (data.error) {
      setError(data.error);
      return;
    }
    setBrandsRrp(Array.isArray(data.brands) ? data.brands : []);
  };

  const alreadyAdded = new Set(brandsRrp.map((b) => b.toLowerCase()));

  const filteredCatalog = brandSearch.trim()
    ? brands.filter(
        (b) =>
          b.api_name !== 'api_whitegoods' &&
          b.brand_name.toLowerCase().includes(brandSearch.toLowerCase())
      )
    : brands.filter((b) => b.api_name !== 'api_whitegoods');

  const visibleRrp = filterInput.trim()
    ? brandsRrp.filter((b) => b.toLowerCase().includes(filterInput.trim().toLowerCase()))
    : brandsRrp;

  const sortedRrp = [...visibleRrp].sort((a, b) => a.localeCompare(b, 'ru'));

  return (
    <>
      <Header
        title="Бренды с РРЦ"
        breadCrumbs={[
          { name: 'Главная', link: '/' },
          { name: 'Бренды и API', link: '/brands' },
          { name: 'Бренды с РРЦ', link: '/brands/use-rrp' },
        ]}
      />
      <div className={s.wrap}>
        <p className={s.hint}>
          Для этих брендов итоговая цена в{' '}
          <code className={s.code}>/goods/master</code> и{' '}
          <code className={s.code}>/goods/master/new</code> — розничная цена
          поставщика (РРЦ), без наценки на дилерскую. Сравнение без учёта
          регистра и точки в конце названия.
        </p>
        <div className={s.toolbar}>
          <form className={s.filter} onSubmit={(e) => e.preventDefault()}>
            <input
              className={s.filterInput}
              value={filterInput}
              onChange={(e) => setFilterInput(e.target.value)}
              placeholder="Поиск по бренду..."
            />
            {filterInput && (
              <button
                type="button"
                className="btn grey"
                onClick={() => setFilterInput('')}
              >
                Сбросить
              </button>
            )}
          </form>
          <button className="btn dark" onClick={openCreate}>
            + Добавить
          </button>
        </div>

        {error && <p className={s.error}>{error}</p>}

        {loading ? (
          <Loader />
        ) : sortedRrp.length === 0 ? (
          <p className={s.empty}>Брендов с РРЦ не найдено</p>
        ) : (
          <div className={s.group}>
            <div className={s.groupHeader}>
              <span className={s.groupTitle}>РРЦ</span>
              <span className={s.groupCount}>{sortedRrp.length}</span>
            </div>
            <ul className={s.list}>
              {sortedRrp.map((brand) => (
                <li key={brand} className={s.item}>
                  <div className={s.itemInfo}>
                    <span className={s.brandName}>{brand}</span>
                  </div>
                  <button
                    className={s.editBtn}
                    onClick={() => handleRemove(brand)}
                    title="Убрать из РРЦ"
                  >
                    {delIcon}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {createShowed && (
        <Popup
          close={() => setCreateShowed(false)}
          title="Добавить бренд в РРЦ"
          loading={createLoading}
        >
          <div className={s.form}>
            <div className={s.brandSelect}>
              <label className={s.formLabel}>Найти в каталоге</label>
              <input
                className={s.filterInput}
                value={brandSearch}
                onChange={(e) => {
                  setBrandSearch(e.target.value);
                  setSelectedBrand(null);
                }}
                placeholder="Поиск бренда..."
              />
              {brandsLoading && <p className={s.empty}>Загрузка...</p>}
              {!brandsLoading && (
                <ul
                  className={s.brandDropdown}
                  style={{
                    display: brandSearch && !selectedBrand ? 'block' : 'none',
                  }}
                >
                  {filteredCatalog.slice(0, 50).map((b) => {
                    const added = alreadyAdded.has((b.brand_name || '').toLowerCase());
                    return (
                      <li
                        key={b.brand_id || b._id}
                        className={s.brandDropdownItem}
                        onClick={() => !added && handleSelectBrand(b)}
                        style={added ? { opacity: 0.45, cursor: 'default' } : undefined}
                      >
                        <span>{b.brand_name}</span>
                        <span className={s.dropdownApi}>
                          {added ? 'уже в РРЦ' : b.api_name}
                        </span>
                      </li>
                    );
                  })}
                  {filteredCatalog.length === 0 && (
                    <li className={s.brandDropdownEmpty}>Не найдено</li>
                  )}
                </ul>
              )}
              {selectedBrand && (
                <p className={s.selectedBrand}>✓ {selectedBrand.brand_name}</p>
              )}
            </div>
            <Input
              label="Название бренда"
              val={manualName}
              setVal={setManualName}
              placeholder="например: kocateq"
            />
            {createError && <p className={s.error}>{createError}</p>}
            <button className="btn dark" onClick={handleCreate}>
              Добавить
            </button>
          </div>
        </Popup>
      )}
    </>
  );
};

export default UseRrp;
